import { useEffect, useMemo, useState } from "react";
import { useDraftPrompt } from "../context/DraftPromptContext.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import DraftPromptItems from "./DraftPromptItems.jsx";
import DraftPromptSocials from "./DraftPromptSocials.jsx";
import DraftPromptSchedule from "./DraftPromptSchedule.jsx";
import DraftPromptMyMedia from "./DraftPromptMyMedia.jsx";
import API from "../config/api.js";

// ─────────────────────────────────────────────────────────────────────────────
// Validation helpers
// ─────────────────────────────────────────────────────────────────────────────
function countHashtags(str) {
  return (str.match(/#\w+/g) || []).length;
}
function countTags(str) {
  return (str.match(/@\w+/g) || []).length;
}

// ─────────────────────────────────────────────────────────────────────────────
// DraftPrompt modal
// ─────────────────────────────────────────────────────────────────────────────
export default function DraftPrompt() {
  const {
    state, patch, open, closeDraftPrompt, clear, save, saving,
  } = useDraftPrompt();
  const { subscription } = useAuth();

  const [showMyMedia, setShowMyMedia] = useState(false);

  // ── Expose show/hide my media on window ──────────────────────────────────
  useEffect(() => {
    window._draft_prompt_show_my_media = () => setShowMyMedia(true);
    window._draft_prompt_hide_my_media = () => setShowMyMedia(false);
    return () => {
      delete window._draft_prompt_show_my_media;
      delete window._draft_prompt_hide_my_media;
    };
  }, []);

  // ── Derived type from items ───────────────────────────────────────────────
  const items = state.items || [];
  const hasVideo = items.some((i) => i.type === "reel" || i.type === "video");
  const isReel = items.length === 1 && hasVideo;

  // Keep state.type in sync
  useEffect(() => {
    let type = "";
    if (items.length === 1) {
      const t = items[0].type;
      type = (t === "reel" || t === "video") ? "reel" : "image";
    } else if (items.length > 1) {
      type = "carousel";
    }
    if (type !== state.type) patch({ type });
  }, [items]); // eslint-disable-line

  // ── Close on Escape ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeDraftPrompt(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeDraftPrompt]);

  // ── Modal size ────────────────────────────────────────────────────────────
  const modalSize = isReel ? "modal-lg" : "modal-xl";

  // ── Validation errors ─────────────────────────────────────────────────────
  const errors = useMemo(() => {
    const errs = [];
    const { content, instagram, linkedin } = state;
    if (instagram && countHashtags(content) > 30)
      errs.push("Your post cannot be published on Instagram because it exceeds the maximum limit of 30 hashtags.");
    if (instagram && countTags(content) > 20)
      errs.push("Your post cannot be published on Instagram because it exceeds the maximum limit of 20 tags.");
    if (instagram && content.length > 2200)
      errs.push("Your caption exceeds 2200 characters and will be cut off on Instagram.");
    if (linkedin && content.length > 900)
      errs.push("Your caption exceeds 1000 characters and will be cut off on LinkedIn.");
    return errs;
  }, [state]);

  const canSchedule = subscription
    && (state.facebook || state.instagram || state.linkedin || state.tiktok)
    && state.content.trim()
    && !errors.some((e) => e.includes("cannot be published"));

  // ── Customize caption ─────────────────────────────────────────────────────
  const handleCustomize = async () => {
    try {
      const fd = new FormData();
      fd.append("caption", state.content);
      const res = await fetch(`${API.assistant}?customizeCaption`, {
        method: "POST", credentials: "include", body: fd,
      });
      const text = await res.text();
      // API returns a JSON-encoded string — parse it, fallback to raw text
      let result = text;
      try { result = JSON.parse(text); } catch (_) {}
      patch({ content: result });
    } catch (err) { console.error("customize:", err); }
  };

  // ── Generate hashtags ─────────────────────────────────────────────────────
  const handleHashtags = async () => {
    try {
      const fd = new FormData();
      fd.append("text", `Generate a list like "#hashtag1 #hashtag2" of 4 hashtags for this Instagram copy: ${state.content}`);
      const res = await fetch(`${API.assistant}?generateContent`, {
        method: "POST", credentials: "include", body: fd,
      });
      const result = await res.text();
      patch({ content: state.content + "\n\n" + result });
    } catch (err) { console.error("hashtags:", err); }
  };

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />

      <div
        className="modal fade show d-block"
        id="draft-prompt"
        tabIndex={-1}
        style={{ zIndex: 1050 }}
        aria-modal="true"
        role="dialog"
        onMouseDown={(e) => {
          // Only close if the mousedown started on the modal backdrop area,
          // not inside the dialog. Using mousedown (not click) avoids closing
          // when the user starts a text selection inside the textarea and
          // drags out — click would fire on the backdrop in that case.
          if (e.target === e.currentTarget) closeDraftPrompt();
        }}
      >
        <div className={`modal-dialog ${modalSize} modal-dialog-centered modal-fullscreen-md-down`}>
          <div className="modal-content rounded-4">
            <div className="modal-body p-0">

              {/* ── Subscription banner ── */}
              {!subscription && (
                <div className="bg-secondary rounded-4 shadow-sm p-3">
                  <div className="row align-items-center g-4">
                    <div className="col-md-9 fs-6 fw-bold">
                      Unlock the full calendar, scheduling, templates and all other features
                    </div>
                    <div className="col-md-3">
                      <a
                        className="btn btn-primary w-100"
                        href="/plans"
                        onClick={(e) => {
                          e.preventDefault();
                          closeDraftPrompt();
                          window.location.href = "/plans";
                        }}
                      >
                        Subscribe Now
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <div className="row g-2 m-0 align-items-stretch">

                {/* ── Left: media items ── */}
                <DraftPromptItems />

                {/* ── Right: details ── */}
                <div className={`${isReel ? "col-lg-7" : "col-lg-6"}`} id="draft-prompt-details">
                  {/* Desktop close */}
                  <button
                    type="button"
                    className="d-none d-sm-block btn btn-close bg-white rounded-circle p-3 position-absolute"
                    style={{ right: -55, height: 22, marginTop: -6 }}
                    onClick={closeDraftPrompt}
                    aria-label="Close"
                  />

                  <div className="d-flex flex-column h-100 position-relative" style={{ overflow: "visible" }}>
                    <div className="flex-grow-1 p-3 pb-0">

                      {/* ── Textarea ── */}
                      <textarea
                        id="draft-prompt-content"
                        name="content"
                        rows={5}
                        className="form-control rounded-4 border h-100"
                        style={{
                          resize: "none",
                          color: "#000",
                          ...(subscription ? {} : { filter: "blur(5px)" }),
                        }}
                        placeholder="Say Something..."
                        value={state.content}
                        onChange={(e) => patch({ content: e.target.value })}
                      />

                      {/* ── My Media panel ── */}
                      {showMyMedia && (
                        <DraftPromptMyMedia onClose={() => setShowMyMedia(false)} />
                      )}
                    </div>

                    {/* ── Validation errors ── */}
                    {errors.map((err, i) => (
                      <div key={i} className="caption-error text-danger d-inline-block lh-1 px-3">
                        <small className="p-2 d-block">
                          <i className="fi fi-rr-exclamation" /> {err}
                        </small>
                      </div>
                    ))}

                    <div className="p-3 pt-2">
                      {/* ── Toolbar ── */}
                      <div className="d-flex align-items-center justify-content-between" id="draft-prompt-action-buttons">
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline-primary rounded-circle me-2"
                            type="button"
                            onClick={handleHashtags}
                            title="Generate hashtags"
                          >
                            <i className="fi fi-rr-hastag text-primary align-text-bottom" />
                          </button>
                          <a
                            className="btn btn-sm btn-outline-primary rounded-circle"
                            href="/captions"
                            title="Browse captions"
                          >
                            <i className="fi fi-rr-text text-primary align-text-bottom" />
                          </a>
                        </div>

                        <div>
                          {subscription && (
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary me-2"
                              onClick={handleCustomize}
                            >
                              Rewrite <i className="fi fi-rr-magic-wand" />
                            </button>
                          )}
                          <a href="/preferences" className="btn btn-sm btn-primary">
                            <i className="fi fi-rr-settings-sliders" />
                          </a>
                        </div>
                      </div>

                      {/* ── Schedule ── */}
                      <DraftPromptSchedule />

                      {/* ── Socials ── */}
                      <DraftPromptSocials />
                    </div>

                    {/* ── Footer actions ── */}
                    <div className="bg-secondary-light rounded-3 p-2 m-2">
                      <div className="row g-2 justify-content-between">
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn btn-secondary w-100 px-2"
                            onClick={clear}
                          >
                            Clear <span className="d-none d-sm-inline-block">All</span>
                          </button>
                        </div>
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn btn-secondary w-100 px-2"
                            onClick={() => save(false, false)}
                            disabled={saving || !state.content.trim()}
                          >
                            Save <span className="d-none d-sm-inline-block">Draft</span>
                          </button>
                        </div>
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => save(true, state.scheduled)}
                            disabled={saving || !canSchedule}
                          >
                            {saving ? (
                              <span className="spinner-border spinner-border-sm" role="status" />
                            ) : state.scheduled ? "Schedule" : "Post Now"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}